// ============================================================
// components/landing/BestOnIPhone.tsx
//
// "The full experience / Best on iPhone" panel and the App
// Store badge and QR card it uses. Shared by the home page and
// the web sign-in page.
// ============================================================
import React, { useId } from 'react';
import { BookUser, Camera, MessagesSquare, Smartphone, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { APP_STORE_URL } from '../common/ComingSoonPage';
import { useIsAndroid } from '../../lib/android';
import { AndroidWaitListButton } from './AndroidWaitList';

/**
 * Apple's official "Download on the App Store" badge: black on light
 * surfaces, white on dark. `inverse` is for surfaces that are dark in light
 * mode (and light in dark mode). One aria-label names the link whichever
 * artwork is visible.
 */
export function AppStoreBadge({ inverse = false, className = 'h-12' }: { inverse?: boolean; className?: string }): React.JSX.Element {
  // STASH is iPhone-only for now: Android gets the wait list instead.
  if (useIsAndroid()) return <AndroidWaitListButton />;
  const black = inverse ? 'hidden dark:block' : 'dark:hidden';
  const white = inverse ? 'dark:hidden' : 'hidden dark:block';
  return (
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download STASH on the App Store"
      className="inline-flex shrink-0 rounded-[0.6rem] transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <img src="/appstore-en-us.svg" alt="" width={120} height={40} className={`${className} w-auto ${black}`} />
      <img src="/appstore-en-us-white.svg" alt="" width={120} height={40} className={`${className} w-auto ${white}`} />
    </a>
  );
}

/**
 * Scan-to-download card. Tablet and desktop only: on a phone you can't scan
 * your own screen, and the badge is right there. The code always sits on
 * white so it scans in dark mode too.
 */
export function AppStoreQr({ inverse = false }: { inverse?: boolean }): React.JSX.Element | null {
  // An iPhone camera code means nothing on Android.
  if (useIsAndroid()) return null;
  return (
    <div
      className={[
        'hidden items-center gap-4 rounded-3xl border p-2.5 pr-5 md:inline-flex',
        inverse ? 'border-background/15 bg-background/10' : 'border-border/60 bg-card/80 shadow-sm backdrop-blur',
      ].join(' ')}
    >
      <span className="rounded-2xl bg-white p-2">
        <img
          src="/appstore-qr-code.png"
          alt="QR code to download STASH on the App Store"
          width={400}
          height={400}
          loading="lazy"
          decoding="async"
          className="h-40 w-40"
        />
      </span>
      <span className="flex flex-col text-left leading-snug">
        <span className="text-sm font-semibold">Scan with your</span>
        <span className="text-sm font-semibold">iPhone camera</span>
        <span className={inverse ? 'text-xs opacity-70' : 'text-xs text-muted-foreground'}>to get the app</span>
      </span>
    </div>
  );
}

const IPHONE_PERKS: { icon: LucideIcon; text: string }[] = [
  { icon: Camera, text: 'Snap & Stash with your camera' },
  { icon: MessagesSquare, text: 'Friends, communities, groups and messages' },
  { icon: BookUser, text: 'StashCards in Contacts, Messages, Mail and Spotlight' },
  { icon: Sparkles, text: 'Ask STASH wherever you are' },
];

export function BestOnIPhone(): React.JSX.Element {
  const titleId = useId();
  return (
    <section
      aria-labelledby={titleId}
      className="relative overflow-hidden rounded-[2rem] bg-foreground px-6 py-12 text-background md:px-12 md:py-14"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[var(--stash-blue)]/40 blur-3xl"
      />
      <div className="relative grid items-center gap-10 md:grid-cols-[0.9fr_1.1fr]">
        <div>
          <div className="flex items-center gap-4">
            <img
              src="/stash-app-icon.png"
              alt=""
              width={1024}
              height={1024}
              loading="lazy"
              decoding="async"
              className="h-16 w-16 shrink-0 drop-shadow-lg md:h-[4.5rem] md:w-[4.5rem]"
            />
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] opacity-70">
                <Smartphone className="h-4 w-4" aria-hidden="true" />
                The full experience
              </span>
              <h2 id={titleId} className="mt-2 mb-0 text-3xl font-bold tracking-tight md:text-4xl">
                Best on iPhone
              </h2>
            </div>
          </div>
          <p className="mt-4 mb-0 text-base leading-relaxed opacity-80">
            The web is a handy companion for browsing your stash. The iPhone app is where STASH comes alive:
            capture, your circle, and answers right where you already look.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <AppStoreBadge inverse />
            <AppStoreQr inverse />
          </div>
        </div>
        <ul className="m-0 grid list-none auto-rows-fr gap-3 p-0 sm:grid-cols-2">
          {IPHONE_PERKS.map(({ icon: Icon, text }) => (
            <li
              key={text}
              className="flex items-start gap-3 rounded-2xl border border-background/10 bg-background/5 p-4 backdrop-blur-sm"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-background/10">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="text-sm font-semibold leading-snug">{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
